import React, { useRef, useEffect } from 'react';
import { MessageSquare, X, Send, Bot, User, ChevronDown, ChevronUp, Settings } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { format, parseISO } from 'date-fns';
import Button from '../ui/Button';
import { useChatStore } from '../../store/chatStore';

const AIChatWidget: React.FC = () => {
  const {
    messages,
    isOpen,
    isTyping,
    suggestions,
    settings,
    addMessage,
    setIsOpen,
    setIsTyping,
    updateSettings
  } = useChatStore();

  const [input, setInput] = React.useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [showSettings, setShowSettings] = React.useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    // Add user message
    addMessage({
      content: input.trim(),
      type: 'user'
    });
    
    setInput('');
    setIsTyping(true);

    // Simulate AI response
    setTimeout(() => {
      const aiResponse = generateDummyResponse(input.trim());
      addMessage({
        content: aiResponse,
        type: 'ai'
      });
      setIsTyping(false);
    }, 1000);
  };

  const generateDummyResponse = (userInput: string): string => {
    const responses = {
      budget: "Based on your spending patterns, I recommend allocating 50% for necessities, 30% for wants, and 20% for savings. This will help you maintain a healthy financial balance.\n\n**Key tips:**\n- Track all expenses\n- Set realistic goals\n- Review monthly",
      invest: "For beginners, I recommend starting with low-cost index funds. They offer good diversification and have historically provided stable returns.\n\n**Consider:**\n- S&P 500 index funds\n- Total market funds\n- Consult a financial advisor",
      save: "Here are some effective saving strategies:\n\n1. **Automate your savings**\n2. Follow the 24-hour rule for large purchases\n3. Use the 50/30/20 budget rule\n4. Track your expenses regularly",
      default: "I'm here to help with your financial questions! You can ask me about:\n\n- Budgeting\n- Investing\n- Saving money\n- Debt management\n- Financial planning"
    };

    const lowercaseInput = userInput.toLowerCase();
    if (lowercaseInput.includes('budget') || lowercaseInput.includes('spend')) {
      return responses.budget;
    } else if (lowercaseInput.includes('invest') || lowercaseInput.includes('stock')) {
      return responses.invest;
    } else if (lowercaseInput.includes('save') || lowercaseInput.includes('saving')) {
      return responses.save;
    }
    return responses.default;
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
    inputRef.current?.focus();
  };

  const positionClass = settings.position === 'bottom-right' ? 'right-4' : 'left-4';

  return (
    <div className={`fixed bottom-4 ${positionClass} z-50`}>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl mb-4 w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden border border-gray-200 dark:border-gray-700"
          >
            {/* Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Bot className="h-5 w-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Financial Assistant</h3>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  >
                    <Settings size={18} />
                  </button>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Settings Panel */}
              <AnimatePresence>
                {showSettings && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="mt-4 space-y-3 overflow-hidden"
                  >
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Position
                      </label>
                      <select
                        value={settings.position}
                        onChange={(e) => updateSettings({ position: e.target.value as 'bottom-right' | 'bottom-left' })}
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                      >
                        <option value="bottom-right">Bottom Right</option>
                        <option value="bottom-left">Bottom Left</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="showAvatar"
                          checked={settings.showAvatar}
                          onChange={(e) => updateSettings({ showAvatar: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="showAvatar" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                          Show Avatars
                        </label>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="showTimestamp"
                          checked={settings.showTimestamp}
                          onChange={(e) => updateSettings({ showTimestamp: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="showTimestamp" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                          Show Timestamps
                        </label>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="enableMarkdown"
                          checked={settings.enableMarkdown}
                          onChange={(e) => updateSettings({ enableMarkdown: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="enableMarkdown" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                          Enable Markdown
                        </label>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Messages */}
            <div className="h-[400px] overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <>
                  <div className="text-center text-gray-500 dark:text-gray-400 mt-8">
                    <Bot className="h-12 w-12 mx-auto mb-4 text-blue-600" />
                    <p className="font-medium">Hi! I'm your AI financial assistant.</p>
                    <p className="text-sm mt-2">Ask me anything about budgeting, investing, or saving money!</p>
                  </div>
                  <div className="mt-8">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Suggested questions:</p>
                    <div className="space-y-2">
                      {suggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className="w-full text-left text-sm p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                messages.map(message => (
                  <div
                    key={message.id}
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`flex items-start space-x-2 max-w-[80%] ${
                        message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                      }`}
                    >
                      {settings.showAvatar && (
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            message.type === 'user'
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                          }`}
                        >
                          {message.type === 'user' ? (
                            <User size={16} />
                          ) : (
                            <Bot size={16} />
                          )}
                        </div>
                      )}
                      <div className="space-y-1">
                        <div
                          className={`p-3 rounded-lg ${
                            message.type === 'user'
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white'
                          }`}
                        >
                          {settings.enableMarkdown ? (
                            <ReactMarkdown>{message.content}</ReactMarkdown>
                          ) : (
                            message.content
                          )}
                        </div>
                        {settings.showTimestamp && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {format(parseISO(message.timestamp), 'h:mm a')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="flex items-center space-x-2">
                    {settings.showAvatar && (
                      <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                        <Bot size={16} className="text-gray-600 dark:text-gray-400" />
                      </div>
                    )}
                    <div className="p-3 rounded-lg bg-gray-100 dark:bg-gray-800">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex space-x-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask a financial question..."
                  className="flex-1 min-h-[40px] max-h-[120px] px-3 py-2 resize-none rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={1}
                />
                <Button
                  type="submit"
                  disabled={!input.trim() || isTyping}
                  className="px-3 py-2 bg-blue-600 hover:bg-blue-700"
                >
                  <Send size={16} />
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-105"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {isOpen ? (
          <ChevronDown size={24} />
        ) : (
          <MessageSquare size={24} />
        )}
      </motion.button>
    </div>
  );
};

export default AIChatWidget;